/**
 * REFRESH TOKEN FLOW OVERVIEW:
 *
 * When a request fails with 401 (token expired):
 *
 * 1. createResponseInterceptor (this file)
 *    - Detects 401 error and decides to handle refresh
 *
 * 2. handleExistingRefresh OR handleNewRefresh (this file)
 *    - handleExistingRefresh: If refresh already in progress, wait for it
 *    - handleNewRefresh: If no refresh in progress, start new one
 *
 * 3. handleTokenRefreshFlow (auth-utils.ts)
 *    - Orchestrates the complete refresh process
 *
 * 4. handleRefreshToken (auth-utils.ts)
 *    - Makes API call to refresh endpoint
 *
 * 5. updateAuthTokens (auth-utils.ts)
 *    - Saves new tokens to localStorage
 *
 * 6. retryRequestWithNewToken (auth-utils.ts)
 *    - Retries original failed request with new token
 *
 * RACE CONDITION PROTECTION:
 * - Only one refresh attempt at a time
 * - All concurrent 401 requests wait for same refresh
 * - State managed via isRefreshing flag and refreshPromise
 */
import { AuthResponse, getLocalStorage, handleLogout } from "@elit-hair/elit-tools";
import { captureException } from "@sentry/react";
import axios, { AxiosError, AxiosInstance, InternalAxiosRequestConfig } from "axios";

import { SETTINGS_KEYS } from "@/constants/settings-keys";
import { AuthMeUserResponse } from "@/types/auth-me.types";
import { hasActivePermissions } from "@/utils/auth-me-permissions.utils";
import {
    getRefreshState,
    handleTokenRefreshFlow,
    resetRefreshState,
    retryRequestWithCurrentToken,
    setRefreshState,
} from "@/utils/auth-token.utils";
import { shouldHandleRefresh, shouldSkipAuthRefresh } from "@/utils/auth.utils";

import { defaultSettings } from "../providers/general/settings-provider";
import { authServiceEndpoints } from "./endpoints";
import { ENV } from "./env";
import { LOCAL_STORAGE_KEYS } from "./local-storage-keys";

// Axios instance without interceptors for permission checks
// This prevents infinite loops when checking permissions on 403 errors
const axiosWithoutInterceptors = axios.create({
    baseURL: ENV.AUTH_SVC_URL,
});

const createRequestInterceptor = async (config: InternalAxiosRequestConfig<unknown>) => {
    const auth = getLocalStorage<AuthResponse | null>(LOCAL_STORAGE_KEYS.AUTH.key, LOCAL_STORAGE_KEYS.AUTH.default);

    if (auth?.accessToken) {
        config.headers.Authorization = auth.accessToken;
    }

    // Add x-brand-name header for call-svc requests
    if (config.baseURL?.includes(ENV.CALL_SVC_URL)) {
        const selectedBrand = getLocalStorage<string | null>(
            SETTINGS_KEYS.SELECTED_BRAND,
            defaultSettings.SELECTED_BRAND
        );
        // Always set a brand name header, use default if none is selected
        config.headers["x-brand-name"] = selectedBrand;

        // Log warning if using default brand name
        if (!selectedBrand) {
            console.warn("No brand name found in local storage, using default brand name:", selectedBrand);
            captureException(
                new Error("No brand name found in local storage, using default brand name: " + selectedBrand)
            );
        }
    }

    return config;
};

/**
 * Handles the case when a token refresh is already in progress
 *
 * USE CASE: Multiple concurrent requests fail with 401 at the same time
 *
 * FLOW:
 * 1. Wait for the existing refresh to complete
 * 2. If refresh succeeds → retry original request with new token
 * 3. If refresh fails → reject with error
 */
const handleExistingRefresh = async (error: AxiosError): Promise<unknown> => {
    console.log("⏳ Waiting for existing refresh...");
    try {
        // STEP 1: Get the existing refresh promise and wait for it
        const { refreshPromise } = getRefreshState();
        const refreshSuccess = await refreshPromise!;

        // STEP 2: If refresh succeeded, retry the original request
        if (refreshSuccess && error.config) {
            return retryRequestWithCurrentToken(error.config);
        }

        // STEP 3: Refresh failed (refreshSuccess is false), reject the request
        return Promise.reject(new Error("Refresh failed"));
    } catch (refreshError) {
        return Promise.reject(refreshError);
    }
};

/**
 * Handles starting a new token refresh
 *
 * USE CASE: First request that fails with 401, or after previous refresh failed
 *
 * FLOW:
 * 1. Start token refresh flow and set refresh state
 * 2. Wait for refresh to complete
 * 3. If refresh succeeds → retry original request with new token
 * 4. If refresh fails → reset state and reject
 */
const handleNewRefresh = async (error: AxiosError): Promise<unknown> => {
    console.log("🔄 Starting new refresh...");

    // STEP 1: Start the token refresh flow and set state
    const refreshPromise = handleTokenRefreshFlow();
    setRefreshState(true, refreshPromise);

    try {
        // STEP 2: Wait for the refresh to complete
        const refreshSuccess = await refreshPromise;
        resetRefreshState();

        // STEP 3: If refresh succeeded, retry the original request
        if (refreshSuccess && error.config) {
            return retryRequestWithCurrentToken(error.config);
        }

        // STEP 4: Refresh failed (refreshSuccess is false), reject the request
        return Promise.reject(new Error("Refresh failed"));
    } catch (refreshError) {
        // STEP 4: Error during refresh, reset state and reject
        resetRefreshState();
        return Promise.reject(refreshError);
    }
};

/**
 * Main response interceptor that handles 401 errors and triggers token refresh
 *
 * FLOW OVERVIEW:
 * 1. Check if error is 401 and should trigger refresh
 * 2. If refresh already in progress → wait for existing refresh
 * 3. If no refresh in progress → start new refresh
 * 4. Retry original request with new token
 */
const createResponseInterceptor = async (error: AxiosError) => {
    const requestUrl = error.config?.url || "";

    // STEP 1: Handle 401 from refresh token endpoint - logout immediately
    if (error.response?.status === 401 && requestUrl.includes(authServiceEndpoints.refreshToken)) {
        handleLogout();
        return Promise.reject(error);
    }

    // STEP 1.5: Handle 403 (Forbidden) - verify user permissions and logout if no permissions
    //
    // IMPORTANT: We use axiosWithoutInterceptors to prevent infinite loops.
    // If we used the regular axios instance, a 403 on /user/me would trigger
    // this interceptor again, creating an infinite loop.
    if (error.response?.status === 403 && !shouldSkipAuthRefresh(error)) {
        // OPTIMIZATION: If 403 comes from /user/me, user definitely has no permissions
        // No need to check again - logout immediately
        if (requestUrl.includes("/user/me")) {
            handleLogout();
            return Promise.reject(error);
        }

        try {
            const auth = getLocalStorage<AuthResponse | null>(
                LOCAL_STORAGE_KEYS.AUTH.key,
                LOCAL_STORAGE_KEYS.AUTH.default
            );

            if (auth?.accessToken) {
                // Use axios instance without interceptors to check user permissions
                // This prevents infinite loops since this request won't trigger interceptors
                const response = await axiosWithoutInterceptors.get<AuthMeUserResponse>("/user/me", {
                    headers: {
                        Authorization: auth.accessToken,
                    },
                });

                // `/user/me` exposes permissions via permissionGroups, not permissionCodes.
                // Logging out on missing permissionCodes wrongly kicked users out on any 403.
                if (!hasActivePermissions(response.data)) {
                    handleLogout();
                    return Promise.reject(error);
                }
            } else {
                handleLogout();
                return Promise.reject(error);
            }
        } catch {
            handleLogout();
            return Promise.reject(error);
        }
    }

    // STEP 2: Check if this error should trigger the refresh flow
    if (shouldHandleRefresh(error, requestUrl)) {
        const { isRefreshing, refreshPromise } = getRefreshState();

        // STEP 2A: If refresh already in progress, wait for it
        if (isRefreshing && refreshPromise) {
            return handleExistingRefresh(error);
        }

        // STEP 2B: If no refresh in progress, start new one
        return handleNewRefresh(error);
    }

    // Not a refreshable error, reject normally
    return Promise.reject(error);
};

export const authSvcAxiosInstance = axios.create({
    baseURL: ENV.AUTH_SVC_URL,
});
export const callSvcAxiosInstance = axios.create({
    baseURL: ENV.CALL_SVC_URL,
});
export const strapiAxiosInstance = axios.create({
    baseURL: ENV.STRAPI_URL,
});

// Map of service patterns to their corresponding axios instances
export const SERVICE_AXIOS_MAP: Record<string, AxiosInstance> = {
    "/auth-svc/": authSvcAxiosInstance,
    "/call-svc/": callSvcAxiosInstance,
};

/**
 * Gets the appropriate axios instance based on the request URL
 * @param url The request URL
 * @returns The corresponding axios instance or callSvcAxiosInstance as default
 */
export const getAxiosInstanceForUrl = (url: string): AxiosInstance => {
    for (const [pattern, instance] of Object.entries(SERVICE_AXIOS_MAP)) {
        if (url.includes(pattern)) {
            return instance;
        }
    }
    // Default to call service if no pattern matches
    return callSvcAxiosInstance;
};

authSvcAxiosInstance.interceptors.request.use(createRequestInterceptor);
authSvcAxiosInstance.interceptors.response.use(undefined, createResponseInterceptor);

callSvcAxiosInstance.interceptors.request.use(createRequestInterceptor);
callSvcAxiosInstance.interceptors.response.use(undefined, createResponseInterceptor);

strapiAxiosInstance.interceptors.request.use(createRequestInterceptor);
strapiAxiosInstance.interceptors.response.use(undefined, createResponseInterceptor);

// Global reusable regex patterns
export const REGEX = {
    validName: /^[a-zA-Z0-9\s\-_]+$/,
};
